import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Stack, Paper, Chip, Avatar,
  IconButton, Tooltip, CircularProgress, Divider, Alert, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel
} from '@mui/material';
import {
  ArrowBack, Refresh, Headphones, CallEnd, PeopleAlt, Person,
  LocalHospital, Warning, Schedule, Circle, Visibility, FiberManualRecord,
  AdminPanelSettings, Logout, PhoneInTalk, AccessTime, TrendingUp
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const REFRESH_INTERVAL = 10000; // 10 seconds

export default function AdminControlRoom() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [inactivePatients, setInactivePatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inactiveDays, setInactiveDays] = useState(7);

  const fetchDashboard = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/live-dashboard');
      setDashboard(data);
    } catch (err) { console.error('Error fetching dashboard:', err); }
  }, []);

  const fetchInactivePatients = useCallback(async () => {
    try {
      const data = await apiRequest(`/admin/inactive-patients?days=${inactiveDays}`);
      setInactivePatients(data.patients || []);
    } catch { setInactivePatients([]); }
  }, [inactiveDays]);

  const fetchSessions = useCallback(async () => {
    try {
      const data = await apiRequest('/admin/sessions');
      setSessions(data.sessions || []);
    } catch { setSessions([]); }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchDashboard(), fetchInactivePatients(), fetchSessions()]);
  }, [fetchDashboard, fetchInactivePatients, fetchSessions]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await refreshAll();
      setLoading(false);
    };
    load();
    const interval = setInterval(refreshAll, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshAll]);

  useEffect(() => { fetchInactivePatients(); }, [inactiveDays, fetchInactivePatients]);

  const handleEndCall = async (callId) => {
    if (!window.confirm('¿Finalizar esta llamada?')) return;
    try {
      await apiRequest(`/admin/calls/${callId}/end`, { method: 'POST' });
      fetchDashboard();
    } catch (err) { console.error('Error ending call:', err); }
  };

  const handleListenCall = (callId) => {
    // Open call in observer mode — reuse the VideoCall component
    navigate(`/video-call/${callId}?mode=observer`);
  };

  const handleCloseSession = async (sessionId) => {
    if (!window.confirm('¿Cerrar esta sesión?')) return;
    try {
      await apiRequest(`/admin/sessions/${sessionId}`, { method: 'DELETE' });
      fetchSessions();
    } catch (err) { console.error('Error closing session:', err); }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '—';
    if (minutes < 1) return '<1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}m`;
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Nunca';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Ahora';
    if (diff < 3600) return `${Math.round(diff / 60)} min`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h`;
    return `${Math.round(diff / 86400)}d`;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/dashboard/employee')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{
                fontWeight: 800,
                background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>Vincula</Typography>
              <Chip label="Control Room" size="small" variant="outlined" icon={<AdminPanelSettings sx={{ fontSize: 14 }} />} />
              <Chip
                icon={<FiberManualRecord sx={{ fontSize: 8 }} />}
                label="En vivo"
                size="small"
                color="success"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip label="Seguridad" size="small" clickable onClick={() => navigate('/admin/security')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Chip label="Auditoría" size="small" clickable onClick={() => navigate('/admin/audit')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Chip label="Config" size="small" clickable onClick={() => navigate('/admin/config')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Chip label="Reportes" size="small" clickable onClick={() => navigate('/admin/reports')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Chip label="Comunicados" size="small" clickable onClick={() => navigate('/admin/announcements')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Chip label="Agente IA" size="small" clickable onClick={() => navigate('/admin/ai-logs')}
                sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              <Tooltip title="Refrescar">
                <IconButton onClick={refreshAll}><Refresh /></IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Real-time Metrics (F3) */}
        {dashboard && (
          <Stack direction="row" spacing={2} sx={{ mb: 3, overflowX: 'auto' }}>
            {[
              { label: 'Llamadas activas', value: dashboard.active_calls?.length || 0, icon: <PhoneInTalk />, color: dashboard.active_calls?.length > 0 ? '#EF4444' : '#10B981' },
              { label: 'En cola', value: dashboard.queue_entries, icon: <Schedule />, color: '#F59E0B' },
              { label: 'Hoy completadas', value: dashboard.completed_today, icon: <TrendingUp />, color: '#2563EB' },
              { label: 'Hoy total', value: dashboard.total_calls_today, icon: <PhoneInTalk />, color: '#475569' },
              { label: 'Duración promedio', value: dashboard.avg_duration_today ? `${Math.round(dashboard.avg_duration_today / 60)} min` : '—', icon: <AccessTime />, color: '#7C3AED' },
              { label: 'Usuarios online', value: dashboard.online_users, icon: <PeopleAlt />, color: '#10B981' },
            ].map((stat) => (
              <Paper key={stat.label} sx={{ p: 2, minWidth: 140, borderRadius: 3, flex: '1 0 auto' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={800} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Active Calls (F12 + F13) */}
        <Paper sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PhoneInTalk sx={{ color: '#EF4444' }} />
              <Typography variant="h6" fontWeight={700}>Llamadas activas</Typography>
              <Chip label={dashboard?.active_calls?.length || 0} size="small" color="error" sx={{ fontWeight: 700 }} />
            </Stack>
          </Box>
          {(!dashboard?.active_calls || dashboard.active_calls.length === 0) ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No hay llamadas activas</Typography>
            </Box>
          ) : (
            dashboard.active_calls.map((call, idx) => (
              <React.Fragment key={call.id}>
                {idx > 0 && <Divider />}
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between"
                  sx={{ p: 2, bgcolor: call.is_stale ? '#FEF2F2' : 'white' }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: call.is_ai_call ? '#EEF2FF' : '#F0FDF4', color: call.is_ai_call ? '#4F46E5' : '#16A34A' }}>
                      {call.is_ai_call ? '🤖' : <Person />}
                    </Avatar>
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontWeight={600}>{call.patient_name}</Typography>
                        <Chip label={call.is_ai_call ? 'IA' : call.employee_name || 'Sin asignar'} size="small"
                          sx={{ height: 20, fontSize: 10, bgcolor: call.is_ai_call ? '#EEF2FF' : '#F0FDF4', color: call.is_ai_call ? '#4F46E5' : '#16A34A' }} />
                        <Chip label={call.status === 'waiting' ? 'En cola' : 'Activa'} size="small"
                          sx={{ height: 20, fontSize: 10, bgcolor: call.status === 'waiting' ? '#FEF3C7' : '#DBEAFE', color: call.status === 'waiting' ? '#D97706' : '#2563EB' }} />
                        {call.is_stale && (
                          <Chip icon={<Warning sx={{ fontSize: 12 }} />} label="Posible colgada" size="small"
                            sx={{ height: 20, fontSize: 10, bgcolor: '#FEE2E2', color: '#DC2626' }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(call.duration_minutes)} · Room: {call.room_id?.substring(0, 12)}...
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: { xs: 1, sm: 0 } }}>
                    {call.status === 'active' && (
                      <Tooltip title="Escuchar (oculto)">
                        <IconButton size="small" onClick={() => handleListenCall(call.id)} sx={{ color: '#2563EB' }}>
                          <Headphones fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Finalizar llamada">
                      <IconButton size="small" onClick={() => handleEndCall(call.id)} sx={{ color: '#EF4444' }}>
                        <CallEnd fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </React.Fragment>
            ))
          )}
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Inactive Patients (F17) */}
          <Paper sx={{ borderRadius: 3, flex: 1, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Warning sx={{ color: '#F59E0B' }} />
                  <Typography variant="h6" fontWeight={700}>Pacientes sin actividad</Typography>
                  <Chip label={inactivePatients.length} size="small" color="warning" sx={{ fontWeight: 700 }} />
                </Stack>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select value={inactiveDays} onChange={(e) => setInactiveDays(e.target.value)}
                    sx={{ fontSize: 12, height: 30 }}>
                    <MenuItem value={3}>3 días</MenuItem>
                    <MenuItem value={7}>7 días</MenuItem>
                    <MenuItem value={14}>14 días</MenuItem>
                    <MenuItem value={30}>30 días</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
            {inactivePatients.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">Todos los pacientes tuvieron actividad reciente</Typography>
              </Box>
            ) : (
              inactivePatients.slice(0, 10).map((p, idx) => (
                <React.Fragment key={p.id}>
                  {idx > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#FEF3C7', color: '#D97706', fontSize: 12 }}>
                        {(p.first_name?.[0] || '?').toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{p.first_name} {p.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.last_call_at ? `Última: ${formatTimeAgo(p.last_call_at)}` : 'Nunca llamó'} · {p.total_calls} llamadas
                        </Typography>
                      </Box>
                    </Stack>
                    {p.days_inactive && (
                      <Chip label={`${Math.round(p.days_inactive)}d`} size="small"
                        sx={{ bgcolor: p.days_inactive > 14 ? '#FEE2E2' : '#FEF3C7', color: p.days_inactive > 14 ? '#DC2626' : '#D97706', fontWeight: 700, fontSize: 11 }} />
                    )}
                  </Stack>
                </React.Fragment>
              ))
            )}
          </Paper>

          {/* Active Sessions (F21) */}
          <Paper sx={{ borderRadius: 3, flex: 1, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Circle sx={{ color: '#10B981', fontSize: 14 }} />
                <Typography variant="h6" fontWeight={700}>Sesiones activas</Typography>
                <Chip label={sessions.length} size="small" color="success" sx={{ fontWeight: 700 }} />
              </Stack>
            </Box>
            {sessions.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" variant="body2">No hay sesiones activas</Typography>
              </Box>
            ) : (
              sessions.slice(0, 15).map((s, idx) => (
                <React.Fragment key={s.id}>
                  {idx > 0 && <Divider />}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, bgcolor: '#EEF2FF', color: '#4F46E5' }}>
                        {(s.user_name?.[0] || '?').toUpperCase()}
                      </Avatar>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" fontWeight={600}>{s.user_name}</Typography>
                          <Chip label={s.user_role} size="small" sx={{ height: 18, fontSize: 9 }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimeAgo(s.last_activity)} · {s.ip_address || '—'}
                        </Typography>
                      </Box>
                    </Stack>
                    <Tooltip title="Cerrar sesión">
                      <IconButton size="small" onClick={() => handleCloseSession(s.id)} sx={{ color: '#EF4444' }}>
                        <Logout fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </React.Fragment>
              ))
            )}
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
